// Accountless engagement: an anonymous visitor id plus the set of gems this
// browser has liked, both kept in localStorage so hearts are correct on the
// very first paint. The server is the source of truth for totals.

const VISITOR_KEY = "vibeary:visitor";
const LIKES_KEY = "vibeary:likes";
const VIEWED_KEY = "vibeary:viewed";
const VIEW_TTL_MS = 12 * 60 * 60 * 1000;

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode: engagement is best-effort.
  }
}

export function visitorId(): string {
  if (typeof localStorage === "undefined") return "";
  let id = "";
  try {
    id = localStorage.getItem(VISITOR_KEY) ?? "";
  } catch {
    return "";
  }
  if (!id) {
    id =
      globalThis.crypto?.randomUUID?.() ??
      `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    try {
      localStorage.setItem(VISITOR_KEY, id);
    } catch {
      // ignore
    }
  }
  return id;
}

export function likedSlugs(): Set<string> {
  if (typeof localStorage === "undefined") return new Set();
  return new Set(read<string[]>(LIKES_KEY, []));
}

export function isLiked(slug: string): boolean {
  return likedSlugs().has(slug);
}

function setLikedLocal(slug: string, liked: boolean) {
  const set = likedSlugs();
  if (liked) set.add(slug);
  else set.delete(slug);
  write(LIKES_KEY, [...set]);
}

/** Optimistic toggle: local state flips first, the server confirms the total. */
export async function toggleLike(
  slug: string,
  liked: boolean,
): Promise<number | null> {
  setLikedLocal(slug, liked);
  const visitor = visitorId();
  if (!visitor) return null;
  try {
    const res = await fetch("/api/public/library/like", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug, visitor, liked }),
    });
    if (!res.ok) return null;
    const json = (await res.json()) as { likes?: number };
    return typeof json.likes === "number" ? json.likes : null;
  } catch {
    return null;
  }
}

/** Fire-and-forget view ping, throttled to once per gem per 12 hours. */
export function trackView(slug: string): void {
  if (typeof localStorage === "undefined") return;
  const seen = read<Record<string, number>>(VIEWED_KEY, {});
  const now = Date.now();
  if (seen[slug] && now - seen[slug] < VIEW_TTL_MS) return;
  seen[slug] = now;
  // Keep the map small: drop anything past the dedupe window.
  for (const [k, t] of Object.entries(seen)) {
    if (now - t > VIEW_TTL_MS) delete seen[k];
  }
  write(VIEWED_KEY, seen);

  const visitor = visitorId();
  if (!visitor) return;
  void fetch("/api/public/library/track", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slug, visitor }),
    keepalive: true,
  }).catch(() => undefined);
}

/** "1.2k" style compact count. */
export function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${Math.round(n / 1000)}k`;
}
